import type { FeatureFlag } from '@pflegenavi/shared/api';
import { FeatureFlagStatus } from '@pflegenavi/shared/api';
import {
  useGetTenantFeatureFlags,
  useUserProfileApi,
  useUserProfileWithContext,
} from '@pflegenavi/frontend/api-nursing-home';
import { useScope } from '@pflegenavi/frontend/nursing-home-context';
import { useAuthenticationInternal } from '@pflegenavi/frontend/authentication';

const useUserFeatureFlag = (featureFlag: FeatureFlag): boolean => {
  const api = useUserProfileApi(true);

  const { data: userProfile } = useUserProfileWithContext({
    enabled: api !== undefined,
    refetchOnWindowFocus: false,
  });
  const auth = useAuthenticationInternal();

  return Boolean(
    (api && userProfile?.featureFlags?.includes(featureFlag)) ||
      auth.user?.attributes?.[`ff.${featureFlag}`]
  );
};

export const useFeatureFlagBase = (
  featureFlag: FeatureFlag
): FeatureFlagStatus => {
  const userFeatureFlag = useUserFeatureFlag(featureFlag);

  const { kind, selectedNursingHome } = useScope();
  const { data: tenantFeatureFlags } = useGetTenantFeatureFlags();

  const scopedFlags =
    kind === 'nursing_home'
      ? selectedNursingHome?.featureFlags
      : tenantFeatureFlags?.data.feature_flags;

  return userFeatureFlag || scopedFlags?.includes(featureFlag)
    ? FeatureFlagStatus.Enabled
    : FeatureFlagStatus.Disabled;
};

/**
 * Like `useFeatureFlagBase`, but never reads the flags of the selected nursing
 * home, so the result does not change when the user switches homes.
 *
 * @example
 * const status = useTenantFeatureFlagBase(FeatureFlag.dev_TenantScope);
 */
export const useTenantFeatureFlagBase = (
  featureFlag: FeatureFlag
): FeatureFlagStatus => {
  const userFeatureFlag = useUserFeatureFlag(featureFlag);

  const { data: tenantFeatureFlags } = useGetTenantFeatureFlags();

  return userFeatureFlag ||
    tenantFeatureFlags?.data.feature_flags.includes(featureFlag)
    ? FeatureFlagStatus.Enabled
    : FeatureFlagStatus.Disabled;
};
